import { useState, useRef, useEffect } from 'react';
import { Input } from 'antd';
import 'src/home/presentation/dashboardPresentation/style.scss';

const { TextArea } = Input;

export const TextAreaWithTags = ({
	sendRewardObject,
	setSendRewardObject,
	availableTags = [],
	cursorPosition,
	setCursorPosition,
	shouldFocusTextArea = false,
	setShouldFocusTextArea,
}) => {
	const textareaRef = useRef(null);
	const dropdownRef = useRef(null);
	const selectedItemRef = useRef(null);
	const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
	const [showHashtagDropdown, setShowHashtagDropdown] = useState(false);
	const [currentHashtag, setCurrentHashtag] = useState('');
	const [hashtagStartPos, setHashtagStartPos] = useState(-1);
	const [selectedHashtagIndex, setSelectedHashtagIndex] = useState(0);
	const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
	const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
	const dropdownPositionSetRef = useRef(false);
	const baseDropdownPositionRef = useRef({ top: 0, scrollTop: 0 });
	const isKeyboardNavigatingRef = useRef(false);

	useEffect(() => {
		let timeoutId;

		if (textareaRef.current && shouldFocusTextArea) {
			const content = sendRewardObject?.content;
			const activeTags = extractActiveHashtags(content?.message || '');

			textareaRef.current.focus();
			setShouldFocusTextArea(false);

			timeoutId = setTimeout(() => {
				if (textareaRef.current) {
					const textarea = textareaRef.current.resizableTextArea?.textArea;
					if (textarea && textarea.setSelectionRange) {
						textarea.setSelectionRange(cursorPosition, cursorPosition);
					}
				}
			}, 0);

			if (content?.message || content?.tags?.length > 0) {
				setSendRewardObject({
					...sendRewardObject,
					content: {
						...content,
						tags: activeTags,
					},
				});
			}
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [shouldFocusTextArea, sendRewardObject?.content?.message]);

	useEffect(() => {
		if (!showHashtagDropdown || !textareaRef.current) return;

		const textarea = textareaRef.current.resizableTextArea?.textArea;
		if (!textarea) return;

		const textareaWrapper = textarea.closest('.hashtag-textarea-wrapper');
		if (!textareaWrapper) return;

		let rafId = null;
		let lastScrollTop = textareaWrapper.scrollTop || 0;

		const updateDropdownPosition = () => {
			const currentScrollTop = textareaWrapper.scrollTop || 0;

			if (currentScrollTop !== lastScrollTop) {
				lastScrollTop = currentScrollTop;
				const newTop = baseDropdownPositionRef.current.top - currentScrollTop;

				setDropdownPosition((prev) => ({
					...prev,
					top: newTop,
				}));
			}
		};

		const handleScroll = () => {
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
			rafId = requestAnimationFrame(updateDropdownPosition);
		};

		textareaWrapper.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			textareaWrapper.removeEventListener('scroll', handleScroll);
			if (rafId) {
				cancelAnimationFrame(rafId);
			}
		};
	}, [showHashtagDropdown]);

	useEffect(() => {
		if (showHashtagDropdown && selectedItemRef.current && dropdownRef.current) {
			const dropdown = dropdownRef.current;
			const selectedItem = selectedItemRef.current;

			const dropdownRect = dropdown.getBoundingClientRect();
			const itemRect = selectedItem.getBoundingClientRect();

			if (itemRect.bottom > dropdownRect.bottom) {
				selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			} else if (itemRect.top < dropdownRect.top) {
				selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
			}
		}
	}, [selectedHashtagIndex, showHashtagDropdown]);

	const detectHashtag = (text, cursorPos) => {
		let hashPos = -1;
		for (let i = cursorPos - 1; i >= 0; i--) {
			if (text[i] === '#') {
				hashPos = i;
				break;
			}
			if (text[i] === ' ' || text[i] === '\n') {
				break;
			}
		}

		if (hashPos === -1) {
			setShowHashtagDropdown(false);
			setHashtagSuggestions([]);
			setCurrentHashtag('');
			setHashtagStartPos(-1);
			setSelectedHashtagIndex(0);
			dropdownPositionSetRef.current = false;
			return;
		}

		const afterHash = text.substring(hashPos + 1, cursorPos);

		if (afterHash.includes(' ') || afterHash.includes('\n')) {
			setShowHashtagDropdown(false);
			setHashtagSuggestions([]);
			setCurrentHashtag('');
			setHashtagStartPos(-1);
			setSelectedHashtagIndex(0);
			dropdownPositionSetRef.current = false;
			return;
		}

		let hashEnd = cursorPos;
		while (
			hashEnd < text.length &&
			text[hashEnd] !== ' ' &&
			text[hashEnd] !== '\n'
		) {
			hashEnd++;
		}
		const completeHashtag = text.substring(hashPos + 1, hashEnd);

		const isCompleteAndValid = availableTags.some(
			(tag) => tag.toLowerCase() === completeHashtag.toLowerCase()
		);

		// If a hashtag is complete, has a match, and the cursor is within it, don't show dropdown
		if (isCompleteAndValid) {
			setShowHashtagDropdown(false);
			setHashtagSuggestions([]);
			setCurrentHashtag('');
			setHashtagStartPos(-1);
			setSelectedHashtagIndex(0);
			dropdownPositionSetRef.current = false;
			return;
		}

		const filtered = availableTags
			.filter((tag) => tag.toLowerCase().startsWith(afterHash.toLowerCase()))
			.sort((a, b) => a.localeCompare(b));

		setCurrentHashtag(afterHash);
		setSelectedHashtagIndex(0);

		if (filtered.length > 0 && availableTags.length > 0) {
			setHashtagSuggestions(filtered);

			const hashtagPositionChanged = hashtagStartPos !== hashPos;
			if (!dropdownPositionSetRef.current || hashtagPositionChanged) {
				if (hashtagPositionChanged) {
					dropdownPositionSetRef.current = false;
				}
				setHashtagStartPos(hashPos);
				setTimeout(() => {
					if (textareaRef.current) {
						const textarea = textareaRef.current.resizableTextArea?.textArea;
						if (textarea) {
							const getCaretCoordinates = () => {
								const properties = [
									'direction',
									'boxSizing',
									'width',
									'height',
									'overflowX',
									'overflowY',
									'borderTopWidth',
									'borderRightWidth',
									'borderBottomWidth',
									'borderLeftWidth',
									'paddingTop',
									'paddingRight',
									'paddingBottom',
									'paddingLeft',
									'fontStyle',
									'fontVariant',
									'fontWeight',
									'fontStretch',
									'fontSize',
									'fontSizeAdjust',
									'lineHeight',
									'fontFamily',
									'textAlign',
									'textTransform',
									'textIndent',
									'textDecoration',
									'letterSpacing',
									'wordSpacing',
								];

								const div = document.createElement('div');
								div.style.position = 'absolute';
								div.style.visibility = 'hidden';
								div.style.whiteSpace = 'pre-wrap';
								div.style.wordWrap = 'break-word';

								const computed = window.getComputedStyle(textarea);
								properties.forEach((prop) => {
									div.style[prop] = computed[prop];
								});

								document.body.appendChild(div);

								const textBeforeCaret = text.substring(0, hashPos + 1);
								div.textContent = textBeforeCaret;

								const span = document.createElement('span');
								span.textContent = '|';
								div.appendChild(span);

								const coordinates = {
									top: span.offsetTop,
									left: span.offsetLeft,
								};

								document.body.removeChild(div);
								return coordinates;
							};

							const coords = getCaretCoordinates();
							const lineHeight =
								parseInt(window.getComputedStyle(textarea).lineHeight) || 22;

							const textareaWrapper = textarea.closest(
								'.hashtag-textarea-wrapper'
							);
							const scrollTop = textareaWrapper ? textareaWrapper.scrollTop : 0;

							baseDropdownPositionRef.current = {
								top: coords.top + lineHeight,
								scrollTop: scrollTop,
							};

							setDropdownPosition({
								top: coords.top + lineHeight - scrollTop,
								left: coords.left - 9, // -9px aligns textarea # with #'s in the dropdown list
							});
							dropdownPositionSetRef.current = true;
							setShowHashtagDropdown(true);
						}
					}
				}, 0);
			} else {
				setShowHashtagDropdown(true);
			}
		} else {
			setShowHashtagDropdown(false);
			setHashtagSuggestions([]);
		}
	};

	const handleHashtagSelect = (tag) => {
		const content = sendRewardObject?.content || {};
		const message = content?.message || '';

		const beforeHash = message.substring(0, hashtagStartPos);
		const afterCursor = message.substring(cursorPosition);
		const newText = beforeHash + '#' + tag + ' ' + afterCursor;

		const activeTags = extractActiveHashtags(newText);

		setSendRewardObject({
			...sendRewardObject,
			content: {
				...content,
				message: newText,
				tags: activeTags,
			},
		});

		const newCursorPos = hashtagStartPos + tag.length + 2; // +2 because '#' and space
		setCursorPosition(newCursorPos);
		setShowHashtagDropdown(false);
		setHashtagSuggestions([]);
		setCurrentHashtag('');
		setHashtagStartPos(-1);
		setSelectedHashtagIndex(0);
		dropdownPositionSetRef.current = false;

		setTimeout(() => {
			if (textareaRef.current) {
				textareaRef.current.focus();
				const textarea = textareaRef.current.resizableTextArea?.textArea;
				if (textarea && textarea.setSelectionRange) {
					textarea.setSelectionRange(newCursorPos, newCursorPos);
				}
			}
		}, 0);
	};

	const extractActiveHashtags = (text) => {
		if (!text || availableTags.length === 0) {
			return [];
		}

		const hashtagPattern = /#(\w+)/g;
		const foundTags = [];
		let match;

		while ((match = hashtagPattern.exec(text)) !== null) {
			const hashtag = match[1];
			const matchingTag = availableTags.find(
				(tag) => tag.toLowerCase() === hashtag.toLowerCase()
			);

			if (matchingTag) {
				foundTags.push(matchingTag);
			}
		}

		return [...new Set(foundTags)];
	};

	const correctHashtagCasing = (text) => {
		if (!text || availableTags.length === 0) {
			return text;
		}

		const hashtagPattern = /#(\w+)/g;
		let correctedText = text;
		const replacements = [];

		let match;
		while ((match = hashtagPattern.exec(text)) !== null) {
			const hashtag = match[1];
			const matchingTag = availableTags.find(
				(tag) => tag.toLowerCase() === hashtag.toLowerCase()
			);

			if (matchingTag && matchingTag !== hashtag) {
				replacements.push({
					start: match.index,
					end: match.index + match[0].length,
					original: match[0],
					replacement: '#' + matchingTag,
				});
			}
		}

		for (let i = replacements.length - 1; i >= 0; i--) {
			const { start, end, replacement } = replacements[i];
			correctedText =
				correctedText.substring(0, start) +
				replacement +
				correctedText.substring(end);
		}

		return correctedText;
	};

	const handleOnChangetextState = (e) => {
		const newCursorPosition = e.target.selectionStart;
		const text = e.target.value;

		// Auto-correct any mismatched casing in hashtags
		const correctedText = correctHashtagCasing(text);
		const activeTags = extractActiveHashtags(correctedText);

		let adjustedCursorPosition = newCursorPosition;
		if (correctedText !== text) {
			const lengthDiff = correctedText.length - text.length;
			adjustedCursorPosition = newCursorPosition + lengthDiff;
		}

		setSendRewardObject({
			...sendRewardObject,
			content: {
				...sendRewardObject?.content,
				message: correctedText,
				tags: activeTags,
			},
		});

		setCursorPosition(adjustedCursorPosition);
		detectHashtag(correctedText, adjustedCursorPosition);

		if (correctedText !== text && textareaRef.current) {
			setTimeout(() => {
				const textarea = textareaRef.current.resizableTextArea?.textArea;
				if (textarea && textarea.setSelectionRange) {
					textarea.setSelectionRange(
						adjustedCursorPosition,
						adjustedCursorPosition
					);
				}
			}, 0);
		}
	};

	const handleTextAreaClick = (e) => {
		const newCursorPosition = e.target.selectionStart;
		const text = e.target.value;

		setCursorPosition(newCursorPosition);
		detectHashtag(text, newCursorPosition);
	};

	const handleContainerClick = (e) => {
		// If the click is in the textarea just let that event occur normally
		const textarea = textareaRef.current?.resizableTextArea?.textArea;
		if (!textarea || e.target === textarea) {
			return;
		}

		// If textarea height doesn't yet fill whole container, make event appear like textarea click
		textareaRef.current.focus();
		const text = sendRewardObject?.content?.message || '';
		const endPosition = text.length;

		setTimeout(() => {
			if (textarea && textarea.setSelectionRange) {
				textarea.setSelectionRange(endPosition, endPosition);
			}
			setCursorPosition(endPosition);
		}, 0);
	};

	const handleTextAreaKeyDown = (e) => {
		if (showHashtagDropdown && hashtagSuggestions.length > 0) {
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				isKeyboardNavigatingRef.current = true;
				setSelectedHashtagIndex((prev) =>
					prev < hashtagSuggestions.length - 1 ? prev + 1 : 0
				);
			} else if (e.key === 'ArrowUp') {
				e.preventDefault();
				isKeyboardNavigatingRef.current = true;
				setSelectedHashtagIndex((prev) =>
					prev > 0 ? prev - 1 : hashtagSuggestions.length - 1
				);
			} else if (e.key === 'ArrowRight') {
				e.preventDefault();
				isKeyboardNavigatingRef.current = true;
				if (hashtagSuggestions.length >= 5) {
					setSelectedHashtagIndex((prev) => {
						const nextIndex = prev + 5;
						return nextIndex >= hashtagSuggestions.length
							? hashtagSuggestions.length - 1
							: nextIndex;
					});
				} else {
					setSelectedHashtagIndex(hashtagSuggestions.length - 1);
				}
			} else if (e.key === 'ArrowLeft') {
				e.preventDefault();
				isKeyboardNavigatingRef.current = true;
				if (hashtagSuggestions.length >= 5) {
					setSelectedHashtagIndex((prev) => {
						const nextIndex = prev - 5;
						return nextIndex < 0 ? 0 : nextIndex;
					});
				} else {
					setSelectedHashtagIndex(0);
				}
			} else if (e.key === 'Enter' || e.key === 'Tab') {
				e.preventDefault();
				handleHashtagSelect(hashtagSuggestions[selectedHashtagIndex]);
			} else if (e.key === 'Escape') {
				setShowHashtagDropdown(false);
				setHashtagSuggestions([]);
				setCurrentHashtag('');
				setHashtagStartPos(-1);
				setSelectedHashtagIndex(0);
				dropdownPositionSetRef.current = false;
			}
		}

		if (e.key === ' ' && showHashtagDropdown) {
			setShowHashtagDropdown(false);
			setHashtagSuggestions([]);
			setCurrentHashtag('');
			setHashtagStartPos(-1);
			setSelectedHashtagIndex(0);
			dropdownPositionSetRef.current = false;
		}
	};

	// Prevent last mouse position from fighting against keyboard controls
	const handleDropdownItemMouseEnter = (index) => {
		if (!isKeyboardNavigatingRef.current) {
			setSelectedHashtagIndex(index);
		}
	};

	// Once the mouse is moved again, we set the ref to false to re-enable selection by mouse
	const handleDropdownMouseMove = () => {
		if (isKeyboardNavigatingRef.current) {
			isKeyboardNavigatingRef.current = false;
		}
	};

	const renderTextWithBlueHashtags = (text) => {
		if (!text || availableTags.length === 0) {
			return text;
		}

		const hashtagPattern = /#(\w+)/g;
		const parts = [];
		let lastIndex = 0;
		let match;

		while ((match = hashtagPattern.exec(text)) !== null) {
			const hashtag = match[1];
			const matchStart = match.index;

			if (matchStart > lastIndex) {
				parts.push(
					<span key={`text-${lastIndex}`}>
						{text.substring(lastIndex, matchStart)}
					</span>
				);
			}

			const isMatch = availableTags.some(
				(tag) => tag.toLowerCase() === hashtag.toLowerCase()
			);

			parts.push(
				<span
					key={`hashtag-${matchStart}`}
					className={isMatch ? 'hashtag-match' : ''}
				>
					{match[0]}
				</span>
			);

			lastIndex = matchStart + match[0].length;
		}

		if (lastIndex < text.length) {
			parts.push(
				<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>
			);
		}

		return parts.length > 0 ? parts : text;
	};

	return (
		<div className="hashtag-resizable-wrapper">
			<div
				className={`hashtag-resizable-container ${isTextAreaFocused ? 'focused' : ''}`}
				onClick={handleContainerClick}
			>
				<div className="hashtag-textarea-wrapper">
					<div className="hashtag-overlay">
						{renderTextWithBlueHashtags(
							sendRewardObject?.content?.message || ''
						)}
					</div>
					<TextArea
						ref={textareaRef}
						value={sendRewardObject?.content?.message || ''}
						onChange={handleOnChangetextState}
						onClick={handleTextAreaClick}
						onKeyDown={handleTextAreaKeyDown}
						onFocus={() => setIsTextAreaFocused(true)}
						onBlur={() => setIsTextAreaFocused(false)}
						rows={4}
						maxLength={1000}
						placeholder="Tell us why you are recognizing this person 🎉"
						className="text-area-input hashtag-textarea"
						spellCheck={false}
					/>
				</div>
			</div>
			{showHashtagDropdown && hashtagSuggestions.length > 0 && (
				<div
					className="hashtag-dropdown"
					ref={dropdownRef}
					style={{
						top: `${dropdownPosition.top}px`,
						left: `${dropdownPosition.left}px`,
					}}
					onMouseMove={handleDropdownMouseMove}
				>
					{hashtagSuggestions.map((tag, index) => {
						const matchLength = currentHashtag.length;
						const matchedPart = tag.substring(0, matchLength);
						const remainingPart = tag.substring(matchLength);

						return (
							<div
								key={index}
								ref={index === selectedHashtagIndex ? selectedItemRef : null}
								className={`hashtag-dropdown-item ${index === selectedHashtagIndex ? 'selected' : ''}`}
								onClick={() => handleHashtagSelect(tag)}
								onMouseEnter={() => handleDropdownItemMouseEnter(index)}
							>
								<strong>#{matchedPart}</strong>
								{remainingPart}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};
